import React from "react";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { euro } from "./serviceBriefingLabels";

/** Línea en edición: los números como texto para poder escribir "12," sin que salte. */
export interface LineDraft {
  concept: string;
  quantity: string;
  unitCost: string;
  unitPrice: string;
}

export const emptyLine = (): LineDraft => ({
  concept: "",
  quantity: "1",
  unitCost: "",
  unitPrice: "",
});

/** Acepta coma decimal y espacios. Lo que no es número cuenta como 0. */
export const parseAmount = (value: string): number => {
  const parsed = Number(value.replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
};

export const lineTotals = (lines: LineDraft[]) => {
  const totalCost = lines.reduce(
    (sum, l) => sum + parseAmount(l.quantity) * parseAmount(l.unitCost),
    0
  );
  const totalPrice = lines.reduce(
    (sum, l) => sum + parseAmount(l.quantity) * parseAmount(l.unitPrice),
    0
  );
  return { totalCost, totalPrice, margin: totalPrice - totalCost };
};

export const isLineValid = (line: LineDraft): boolean =>
  line.concept.trim() !== "" &&
  parseAmount(line.quantity) > 0 &&
  line.unitCost.trim() !== "" &&
  parseAmount(line.unitCost) >= 0 &&
  line.unitPrice.trim() !== "" &&
  parseAmount(line.unitPrice) >= 0;

interface Props {
  lines: LineDraft[];
  onChange: (lines: LineDraft[]) => void;
  /**
   * Las líneas vienen de una OC: concepto, cantidad y precio son los de la OC y solo se
   * rellena el coste. Tampoco se añaden ni se quitan líneas.
   */
  fromOc?: boolean;
}

/** Concepto, cantidad, coste y precio, como las líneas de una OC de creatividades. */
const ServiceBriefingLinesTable: React.FC<Props> = ({
  lines,
  onChange,
  fromOc = false,
}) => {
  const { t } = useTranslation();
  const totals = lineTotals(lines);

  const update = (index: number, patch: Partial<LineDraft>) =>
    onChange(lines.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const amountField = (
    index: number,
    field: "quantity" | "unitCost" | "unitPrice",
    label: string
  ) =>
    fromOc && field !== "unitCost" ? (
      field === "unitPrice" ? (
        euro.format(parseAmount(lines[index][field]))
      ) : (
        lines[index][field]
      )
    ) : (
      <TextField
        size="small"
        value={lines[index][field]}
        onChange={(e) => update(index, { [field]: e.target.value })}
        inputProps={{
          inputMode: "decimal",
          "aria-label": label,
          style: { textAlign: "right" },
        }}
        sx={{ width: field === "quantity" ? 80 : 120 }}
      />
    );

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {t("serviceBriefings.lines.concept", "Concepto")}
              </TableCell>
              <TableCell align="right">
                {t("serviceBriefings.lines.quantity", "Cantidad")}
              </TableCell>
              <TableCell align="right">
                {t("serviceBriefings.lines.unitCost", "Coste unitario")}
              </TableCell>
              <TableCell align="right">
                {t("serviceBriefings.lines.unitPrice", "Precio unitario")}
              </TableCell>
              <TableCell align="right">
                {t("serviceBriefings.lines.totalCost", "Coste")}
              </TableCell>
              <TableCell align="right">
                {t("serviceBriefings.lines.totalPrice", "Precio")}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, index) => (
              <TableRow key={index}>
                <TableCell sx={{ minWidth: 220 }}>
                  {fromOc ? (
                    line.concept
                  ) : (
                    <TextField
                      size="small"
                      fullWidth
                      value={line.concept}
                      placeholder={t(
                        "serviceBriefings.lines.conceptPlaceholder",
                        "Ej.: adaptación de banners"
                      )}
                      onChange={(e) =>
                        update(index, { concept: e.target.value })
                      }
                      inputProps={{
                        "aria-label": t(
                          "serviceBriefings.lines.concept",
                          "Concepto"
                        ),
                      }}
                    />
                  )}
                </TableCell>
                <TableCell align="right">
                  {amountField(
                    index,
                    "quantity",
                    t("serviceBriefings.lines.quantity", "Cantidad")
                  )}
                </TableCell>
                <TableCell align="right">
                  {amountField(
                    index,
                    "unitCost",
                    t("serviceBriefings.lines.unitCost", "Coste unitario")
                  )}
                </TableCell>
                <TableCell align="right">
                  {amountField(
                    index,
                    "unitPrice",
                    t("serviceBriefings.lines.unitPrice", "Precio unitario")
                  )}
                </TableCell>
                <TableCell align="right">
                  {euro.format(
                    parseAmount(line.quantity) * parseAmount(line.unitCost)
                  )}
                </TableCell>
                <TableCell align="right">
                  {euro.format(
                    parseAmount(line.quantity) * parseAmount(line.unitPrice)
                  )}
                </TableCell>
                <TableCell padding="checkbox">
                  {!fromOc && (
                    <Tooltip
                      title={t("serviceBriefings.lines.remove", "Quitar línea")}
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          onChange(lines.filter((_, i) => i !== index))
                        }
                        aria-label={t(
                          "serviceBriefings.lines.remove",
                          "Quitar línea"
                        )}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {lines.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">
                    {t("serviceBriefings.lines.empty", "Sin líneas todavía.")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={4} align="right">
                <strong>{t("serviceBriefings.lines.total", "Total")}</strong>
              </TableCell>
              <TableCell align="right">
                <strong>{euro.format(totals.totalCost)}</strong>
              </TableCell>
              <TableCell align="right">
                <strong>{euro.format(totals.totalPrice)}</strong>
              </TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell colSpan={5} align="right">
                {t("serviceBriefings.lines.margin", "Margen")}
              </TableCell>
              <TableCell
                align="right"
                sx={{ color: totals.margin < 0 ? "error.main" : undefined }}
              >
                {euro.format(totals.margin)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      {!fromOc && (
        <Button
          startIcon={<AddIcon />}
          onClick={() => onChange([...lines, emptyLine()])}
          sx={{ mt: 1 }}
        >
          {t("serviceBriefings.lines.add", "Añadir línea")}
        </Button>
      )}
    </>
  );
};

export default ServiceBriefingLinesTable;
